import { z } from 'zod';

import { Constants } from '@/lib/constants';
import { sanitizeClientInput } from '@/utils/sanitizeClientInput';

/////////////////////////////////////////////////////////////
// Define the FormField type
/////////////////////////////////////////////////////////////

export type FormField = {
  Name: string;
  component:
    | 'Checkbox'
    | 'Checkbox Group'
    | 'Dropdown'
    | 'Input'
    | 'Input Group'
    | 'Input Email'
    | 'Input Number'
    | 'Input URL'
    | 'Radio Button'
    | 'Text Area'

    // Validation not required for these components
    | 'Title And Content';

  Required: boolean;
  InputGroup: FormField[];
};

/////////////////////////////////////////////////////////////
// Function to dynamically create Zod schemas based on form configuration
/////////////////////////////////////////////////////////////

const createFieldSchema = (field: FormField): z.ZodTypeAny => {
  switch (field.component) {
    case 'Checkbox':
      return field.Required
        ? z.boolean().refine(value => value, {
            message: Constants.Input.ValidationErrors.EmptySelection,
          })
        : z.boolean().optional();

    case 'Checkbox Group':
      return field.Required
        ? z
            .array(z.union([z.string(), z.boolean()]))
            .superRefine((array: (string | boolean)[], ctx) => {
              // Ensure at least one checkbox is selected (1 non-empty string in the array)
              const hasValidSelection: boolean = array.some(
                (value: string | boolean) =>
                  typeof value === 'string' && value.trim().length > 0
              );

              // Validation for no valid string or boolean selected
              if (!hasValidSelection) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: Constants.Input.ValidationErrors.EmptySelection,
                });
              }
            })
        : z.array(z.union([z.string(), z.boolean()])).optional();

    case 'Dropdown':
      return field.Required
        ? z.string().min(1, Constants.Input.ValidationErrors.EmptySelection)
        : z.string().optional();

    case 'Input':
      return field.Required
        ? z
            .string()
            .min(1, Constants.Input.ValidationErrors.EmptyInput)
            .refine(
              val => sanitizeClientInput(val) === val, // no HTML removed = safe input
              {
                message: Constants.Input.ValidationErrors.NoHtmlAllowed,
              }
            )
        : z
            .string()
            .optional()
            .refine(val => !val || sanitizeClientInput(val) === val, {
              message: Constants.Input.ValidationErrors.NoHtmlAllowed,
            });

    case 'Input Group':
      return z.object(
        field.InputGroup.reduce(
          (acc, subField) => {
            acc[subField.Name] = createFieldSchema(subField);

            return acc;
          },
          {} as Record<string, z.ZodTypeAny>
        )
      );

    case 'Input Email':
      return (
        z
          .string()
          .email(Constants.Input.ValidationErrors.InvalidEmail)
          // check email format
          .refine(
            value => /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value),
            Constants.Input.ValidationErrors.InvalidEmail
          )
      );

    case 'Input Number':
      return field.Required
        ? z.coerce
            .number()
            .min(0, Constants.Input.ValidationErrors.InvalidInput)
        : z.coerce.number().optional();

    case 'Input URL':
      const validateUrl = (value: string, ctx: z.RefinementCtx) => {
        // URL regex pattern for url validation
        const patterns = {
          linkedIn:
            /^(https?:\/\/)?(www\.)?linkedin\.com\/in\/[a-zA-Z0-9-]+\/?$/,
          generic: /^(https?:\/\/)([a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+)(\/\S*)?$/,
        };

        const isLinkedIn = field.Name === 'linkedIn';
        const validUrl = isLinkedIn
          ? patterns.linkedIn.test(value)
          : patterns.generic.test(value);

        if (!validUrl) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: isLinkedIn
              ? Constants.Input.ValidationErrors.InvalidLinkedInUrl
              : Constants.Input.ValidationErrors.InvalidUrl,
          });
        }
      };

      return field.Required
        ? z.string().superRefine((value, ctx) => validateUrl(value, ctx))
        : z.string().superRefine((value, ctx) => {
            if (!value) return true;
            validateUrl(value, ctx);
          });

    case 'Radio Button':
      return field.Required
        ? z.string().min(1, Constants.Input.ValidationErrors.EmptySelection)
        : z.string().optional();

    case 'Text Area':
      return field.Required
        ? z
            .string()
            .min(1, Constants.Input.ValidationErrors.EmptyInput)
            .refine(
              val => sanitizeClientInput(val) === val, // no HTML removed = safe input
              {
                message: Constants.Input.ValidationErrors.NoHtmlAllowed,
              }
            )
        : z
            .string()
            .optional()
            .refine(val => !val || sanitizeClientInput(val) === val, {
              message: Constants.Input.ValidationErrors.NoHtmlAllowed,
            });

    case 'Title And Content':
      return z.void(); // Returning z.void() to satisfy ZodTypeAny

    default:
      throw new Error(`Unsupported component: ${field.component}`);
  }
};

/////////////////////////////////////////////////////////////
// Function to generate Zod schema dynamically
/////////////////////////////////////////////////////////////

export const generateFormDetailsSchema = (
  formFields: FormField[]
): z.ZodObject<any> => {
  const schema: Record<string, z.ZodTypeAny> = {};

  formFields.forEach(field => {
    // Skip 'Title and Content' fields
    if (field.component === 'Title And Content') {
      return;
    }

    if (field.component === 'Input Group' && Array.isArray(field.InputGroup)) {
      // Iterate over fields inside the InputGroup and add them to the schema
      field.InputGroup.forEach(inputField => {
        schema[inputField.Name] = createFieldSchema(inputField);
      });
    } else {
      schema[field.Name] = createFieldSchema(field);
    }
  });

  return z.object(schema);
};

/////////////////////////////////////////////////////////////
// Enum for Webform types
/////////////////////////////////////////////////////////////

export enum WebformTypeEnum {
  FormDetails = 'formDetails',
  ContactDetails = 'contactDetails',
}

/////////////////////////////////////////////////////////////
// Default values for static form fields
/////////////////////////////////////////////////////////////

export const WebformDefaultFormValues = {
  formConfig: {
    formName: '',
    formPageFieldNamesAndLabels: [] as {
      name: string;
      label: string;
    }[],
    contactDetailsPageFieldNamesAndLabels: [] as {
      name: string;
      label: string;
    }[],
  },
  formDetails: {} as { [key: string]: string },
  contactDetails: {} as { [key: string]: string },
};
